import { useEffect, useRef, useState } from 'react'
import { Box, Typography, useMediaQuery, useTheme, FormControl, Select, MenuItem, IconButton } from '@mui/material'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import VideocamIcon from '@mui/icons-material/Videocam'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import {
  setSelectedCameraInLocalStorage,
  setSelectedMicrophoneInLocalStorage,
} from 'src/consumer/services/localstorageServices'

/**
 * CameraPreview Component
 * Shows a live camera preview to help users verify their camera is working
 * before joining the session. This helps prevent race conditions by ensuring
 * the camera is properly initialized.
 */
const CameraPreview = ({
  selectedCamera,
  selectedMicrophone,
  availableCameras,
  availableMicrophones,
  setSelectedCamera,
  setSelectedMicrophone,
  userHasAllowedVideo,
  userHasAllowedAudio,
  isVideoCallScreen,
  meetingSrc,
  microphoneError,
  cameraError,
  setUserHasAllowedAudio,
  setUserHasAllowedVideo,
  setGiveCameraPermissionDialogOpen,
  setGiveMicrophonePermissionDialogOpen,
}) => {
  const videoRef = useRef(null)
  const [previewStream, setPreviewStream] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  // Calculate icon size based on screen size - proportional to video preview
  // Video preview is 400px wide on desktop, so icons should be smaller
  // Mobile: ~24px, Tablet: ~26px, Desktop: ~28px (smaller for better proportion)
  const iconSize = isMobile ? '24px' : isTablet ? '26px' : '28px'

  // Only show preview for video call sessions (not offline)
  const shouldShowPreview = isVideoCallScreen && meetingSrc !== 'offline'

  // Use ref to track current stream for cleanup
  const streamRef = useRef(null)

  useEffect(() => {
    let currentStream = null

    // Only start preview if:
    // 1. It's a video call screen
    // 2. Camera is enabled
    // 3. A camera is selected
    // 4. Video element is available
    if (shouldShowPreview && userHasAllowedVideo && selectedCamera && videoRef.current) {
      setIsLoading(true)
      setError(null)

      const startPreview = async () => {
        try {
          // Stop any existing stream first
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
          }

          // Request camera access
          const stream = await navigator.mediaDevices.getUserMedia({
            video: selectedCamera ? { deviceId: { exact: selectedCamera } } : true,
            audio: false, // Only preview video, not audio
          })

          if (videoRef.current) {
            videoRef.current.srcObject = stream
            setPreviewStream(stream)
            streamRef.current = stream
            currentStream = stream
            setIsLoading(false)
          }
        } catch (err) {
          console.error('Error starting camera preview:', err)
          setError(err.message || 'Failed to access camera')
          setIsLoading(false)
          
          // Clean up if there was an error
          if (currentStream) {
            currentStream.getTracks().forEach(track => track.stop())
          }
        }
      }

      startPreview()
    } else {
      // Stop preview when conditions aren't met
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
      setPreviewStream(null)
      setIsLoading(false)
      setError(null)
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop())
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [shouldShowPreview, userHasAllowedVideo, selectedCamera])

  // Don't render if it's not a video call screen
  if (!shouldShowPreview) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '500px', md: '600px' },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '12px', md: '16px' },
      }}
    >
      {/* Video Preview Frame */}
      <Box
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          borderRadius: { xs: '20px', md: '24px' },
          overflow: 'hidden',
          background: '#000',
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '2px solid rgba(139, 92, 246, 0.2)',
        }}
      >
      {/* Video Element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: previewStream && !isLoading && !error ? 'block' : 'none',
        }}
      />

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid #fff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
            Starting camera...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            flexDirection: 'column',
            gap: 1,
            p: 2,
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.7)' }} />
          <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center' }}>
            Camera unavailable
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Camera Off State */}
      {!userHasAllowedVideo && !isLoading && !error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            color: '#fff',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.5)' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
            Camera is off
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Enable camera to see preview
          </Typography>
        </Box>
      )}

      {/* No Camera Selected State */}
      {userHasAllowedVideo && !selectedCamera && !isLoading && !error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            color: '#fff',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.5)' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
            No camera selected
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Select a camera from the dropdown
          </Typography>
        </Box>
      )}

      </Box>

      {/* Unified Controls - Toggle Buttons + Dropdowns Below Video Frame */}
      {(availableCameras?.length > 0 || availableMicrophones?.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '10px', md: '12px' },
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          {/* Camera Control - Toggle + Dropdown */}
          {availableCameras?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '8px', md: '10px' },
                flex: { xs: '1', sm: '0 1 auto' },
                minWidth: 0,
              }}
            >
              <IconButton
                onClick={() => {
                  if (cameraError) {
                    setGiveCameraPermissionDialogOpen(true)
                  } else {
                    setUserHasAllowedVideo((prev) => !prev)
                  }
                }}
                sx={{
                  width: { xs: '40px', md: '44px' },
                  height: { xs: '40px', md: '44px' },
                  borderRadius: '12px',
                  backgroundColor: cameraError
                    ? 'rgba(239, 68, 68, 0.1)'
                    : userHasAllowedVideo
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(107, 114, 128, 0.1)',
                  border: `2px solid ${
                    cameraError
                      ? 'rgba(239, 68, 68, 0.3)'
                      : userHasAllowedVideo
                      ? 'rgba(16, 185, 129, 0.3)'
                      : 'rgba(107, 114, 128, 0.3)'
                  }`,
                  color: cameraError
                    ? '#ef4444'
                    : userHasAllowedVideo
                    ? '#10b981'
                    : '#6b7280',
                  '&:hover': {
                    backgroundColor: cameraError
                      ? 'rgba(239, 68, 68, 0.15)'
                      : userHasAllowedVideo
                      ? 'rgba(16, 185, 129, 0.15)'
                      : 'rgba(107, 114, 128, 0.15)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
              >
                {userHasAllowedVideo && !cameraError ? (
                  <VideocamIcon sx={{ fontSize: { xs: '20px', md: '22px' } }} />
                ) : (
                  <VideocamOffIcon sx={{ fontSize: { xs: '20px', md: '22px' } }} />
                )}
              </IconButton>
              <FormControl
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    fontSize: { xs: '12px', md: '13px' },
                    height: { xs: '40px', md: '44px' },
                    '& fieldset': {
                      borderColor: 'rgba(99, 102, 241, 0.2)',
                      borderWidth: '1.5px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(99, 102, 241, 0.6)',
                      borderWidth: '2px',
                    },
                  },
                }}
              >
                <Select
                  value={selectedCamera || ''}
                  onChange={(e) => {
                    setSelectedCameraInLocalStorage(e.target.value)
                    setSelectedCamera(e.target.value)
                  }}
                  displayEmpty
                  disabled={!userHasAllowedVideo && !cameraError}
                  renderValue={(value) => {
                    const camera = availableCameras.find((cam) => cam.deviceId === value)
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideocamIcon sx={{ fontSize: '16px', color: '#6366f1', flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: !userHasAllowedVideo && !cameraError ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                          }}
                        >
                          {camera?.label || 'Select Camera'}
                        </Typography>
                      </Box>
                    )
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {availableCameras.map((camera) => (
                    <MenuItem
                      key={camera.deviceId}
                      value={camera.deviceId}
                      sx={{
                        fontSize: { xs: '12px', md: '13px' },
                        py: { xs: 0.75, md: 1 },
                        minHeight: { xs: '36px', md: '40px' },
                        '&:hover': {
                          backgroundColor: 'rgba(99, 102, 241, 0.08)',
                        },
                      }}
                    >
                      {camera.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Microphone Control - Toggle + Dropdown */}
          {availableMicrophones?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '8px', md: '10px' },
                flex: { xs: '1', sm: '0 1 auto' },
                minWidth: 0,
              }}
            >
              <IconButton
                onClick={() => {
                  if (microphoneError) {
                    setGiveMicrophonePermissionDialogOpen(true)
                  } else {
                    setUserHasAllowedAudio((prev) => !prev)
                  }
                }}
                sx={{
                  width: { xs: '40px', md: '44px' },
                  height: { xs: '40px', md: '44px' },
                  borderRadius: '12px',
                  backgroundColor: microphoneError
                    ? 'rgba(239, 68, 68, 0.1)'
                    : userHasAllowedAudio
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(107, 114, 128, 0.1)',
                  border: `2px solid ${
                    microphoneError
                      ? 'rgba(239, 68, 68, 0.3)'
                      : userHasAllowedAudio
                      ? 'rgba(16, 185, 129, 0.3)'
                      : 'rgba(107, 114, 128, 0.3)'
                  }`,
                  color: microphoneError
                    ? '#ef4444'
                    : userHasAllowedAudio
                    ? '#10b981'
                    : '#6b7280',
                  '&:hover': {
                    backgroundColor: microphoneError
                      ? 'rgba(239, 68, 68, 0.15)'
                      : userHasAllowedAudio
                      ? 'rgba(16, 185, 129, 0.15)'
                      : 'rgba(107, 114, 128, 0.15)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
              >
                {userHasAllowedAudio && !microphoneError ? (
                  <MicIcon sx={{ fontSize: { xs: '20px', md: '22px' } }} />
                ) : (
                  <MicOffIcon sx={{ fontSize: { xs: '20px', md: '22px' } }} />
                )}
              </IconButton>
              <FormControl
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    fontSize: { xs: '12px', md: '13px' },
                    height: { xs: '40px', md: '44px' },
                    '& fieldset': {
                      borderColor: 'rgba(99, 102, 241, 0.2)',
                      borderWidth: '1.5px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(99, 102, 241, 0.6)',
                      borderWidth: '2px',
                    },
                  },
                }}
              >
                <Select
                  value={selectedMicrophone || ''}
                  onChange={(e) => {
                    setSelectedMicrophoneInLocalStorage(e.target.value)
                    setSelectedMicrophone(e.target.value)
                  }}
                  displayEmpty
                  disabled={!userHasAllowedAudio && !microphoneError}
                  renderValue={(value) => {
                    const mic = availableMicrophones.find((m) => m.deviceId === value)
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MicIcon sx={{ fontSize: '16px', color: '#6366f1', flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: !userHasAllowedAudio && !microphoneError ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                          }}
                        >
                          {mic?.label || 'Select Microphone'}
                        </Typography>
                      </Box>
                    )
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {availableMicrophones.map((mic) => (
                    <MenuItem
                      key={mic.deviceId}
                      value={mic.deviceId}
                      sx={{
                        fontSize: { xs: '12px', md: '13px' },
                        py: { xs: 0.75, md: 1 },
                        minHeight: { xs: '36px', md: '40px' },
                        '&:hover': {
                          backgroundColor: 'rgba(99, 102, 241, 0.08)',
                        },
                      }}
                    >
                      {mic.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default CameraPreview

